// src/components/Dashboard/DocumentSignature.jsx
//
// Estado de firma de un documento del reclamo (LOR o SPOL) y sus acciones,
// en orden: generar el PDF (aquí mismo, con los datos del reclamo) → marcar
// enviado a firmar → respaldar el PDF firmado → abrir el firmado.
// Lee el estado de claimRecord.signedDocuments; los cambios se devuelven al
// padre (onClaimChange) para que todas las vistas de la ficha queden al día.
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { attachSignedDocument, markDocumentSent } from '../../services/signedDocumentService';
import { openBackup, validateBackupFile } from '../../services/backupService';
import { markDocumentGenerated } from '../../services/signedDocumentService';
import { getStepData } from '../../services/flowService';
import { buildClaimDocument, claimDocumentFileName, renderClaimDocument } from '../../documents/claimDocuments';
import { downloadPdf } from '../../documents/pdfFill';
import DocumentPreflight, { saveDocumentChanges } from './DocumentPreflight';
import './ClaimFlow.css';
import { localTodayISO } from '../../utils/dateHelpers';

const today = () => localTodayISO();

const LABELS = {
  lor: { signed: ['Firmada ✓', 'Signed ✓'], signedAction: ['Subir LOR firmada', 'Upload signed LOR'] },
  spol: { signed: ['Firmada y notarizada ✓', 'Signed & notarized ✓'], signedAction: ['Subir SPOL firmada', 'Upload signed SPOL'] }
};

// inDocuments: dentro del panel Documentos, que ya tiene su botón Generar y su lista de faltantes.
const DocumentSignature = ({ claimRecord, docType, language, isAdmin, onClaimChange, inDocuments = false }) => {
  const es = language === 'es';
  const t = pair => (es ? pair[0] : pair[1]);
  const state = claimRecord.signedDocuments?.[docType] || {};
  const [action, setAction] = useState(null); // { type: 'send'|'sign', date, url, file }
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preflight, setPreflight] = useState(null); // { estimate } mientras el resumen está abierto
  const missing = useMemo(() => buildClaimDocument(docType, claimRecord, language).missing || [], [docType, claimRecord, language]);

  // Primero el resumen (DocumentPreflight): ahí se completan los datos que falten.
  const openPreflight = async () => {
    setGenerating(true);
    const estimate = docType === 'spol' ? await getStepData('claim', claimRecord.id, 'rs_pol').catch(() => ({})) : {};
    setGenerating(false);
    setPreflight({ estimate: estimate || {} });
  };

  const generate = async changes => {
    setGenerating(true);
    try {
      const saved = await saveDocumentChanges(claimRecord, preflight.estimate, changes);
      if (Object.keys(saved.claimChanges).length && onClaimChange) onClaimChange(saved.claimChanges);
      setPreflight(null);
      const document = buildClaimDocument(docType, saved.claimRecord, language, saved.estimate);
      downloadPdf(await renderClaimDocument(document), claimDocumentFileName(document, saved.claimRecord));
      const generated = await markDocumentGenerated(saved.claimRecord, docType);
      if (generated && onClaimChange) onClaimChange(generated);
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(es ? 'No se pudo generar el PDF' : 'Could not generate the PDF');
    } finally {
      setGenerating(false);
    }
  };

  const statusLabel = {
    generated: ['PDF generado', 'PDF generated'],
    sent: ['Enviada a firmar', 'Sent for signature'],
    signed: LABELS[docType].signed
  }[state.status];

  const confirm = async () => {
    if (action.type === 'sign') {
      try { validateBackupFile(action.file); } catch (error) { toast.error(error.message); return; }
    }
    setBusy(true);
    try {
      const changes = action.type === 'send'
        ? await markDocumentSent(claimRecord, docType, { sentDate: action.date, signatureUrl: action.url })
        : await attachSignedDocument(claimRecord, docType, action.file, action.date);
      if (onClaimChange) onClaimChange(changes);
      toast.success(action.type === 'send' ? (es ? 'Marcada como enviada a firmar' : 'Marked as sent') : (es ? 'Documento firmado respaldado' : 'Signed document backed up'));
      setAction(null);
    } catch (error) {
      console.error('Signature action failed:', error);
      toast.error(es ? `No se pudo completar: ${error.message}` : `Could not complete: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="doc-signature">
        {statusLabel && <span className={`cd-status-badge ${state.status === 'signed' ? 'cd-status-paid' : 'cd-status-pending'}`}>{t(statusLabel)}</span>}
        {state.sentDate && <span className="doc-signature-meta">{es ? 'Enviada' : 'Sent'}: {state.sentDate}</span>}
        {state.docusignSignedDate && state.status !== 'signed' && <span className="doc-signature-meta">{es ? `Firmada en DocuSign el ${state.docusignSignedDate}. Sube el PDF firmado.` : `Signed in DocuSign on ${state.docusignSignedDate}. Upload the signed PDF.`}</span>}
        {state.signedDate && <span className="doc-signature-meta">{es ? 'Firmada' : 'Signed'}: {state.signedDate}</span>}
        {(state.signatureUrl || state.docusignUrl) && <a href={state.signatureUrl || state.docusignUrl} target="_blank" rel="noopener noreferrer" className="cd-detail-crm-link">{es ? 'Documento enviado' : 'Sent document'} <i className="fa-solid fa-arrow-up-right-from-square"></i></a>}
        {state.signedPdf?.storageKey && (
          <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(state.signedPdf.storageKey).catch(e => toast.error(e.message))}>
            <i className="fa-solid fa-file-signature"></i> {es ? 'Ver firmada' : 'View signed'}
          </button>
        )}
        {/* Orden: generar y descargar el PDF → subir firmada (marcar enviada es opcional) */}
        {isAdmin && !action && state.status === 'generated' && (
          <button type="button" className="cd-btn" onClick={() => setAction({ type: 'send', date: today(), url: state.signatureUrl || state.docusignUrl || '' })}><i className="fa-solid fa-paper-plane"></i> {es ? 'Marcar enviada (opcional)' : 'Mark as sent (optional)'}</button>
        )}
        {isAdmin && !action && (state.status === 'generated' || state.status === 'sent') && (
          <button type="button" className="cd-btn cd-btn-check" onClick={() => setAction({ type: 'sign', date: today(), file: null })}><i className="fa-solid fa-upload"></i> {t(LABELS[docType].signedAction)}</button>
        )}
        {!inDocuments && isAdmin && !action && state.status !== 'signed' && state.status !== 'sent' && (
          <button type="button" className={`cd-btn ${state.status ? '' : 'cd-btn-check'}`} disabled={generating} onClick={openPreflight}>
            <i className="fa-solid fa-file-pdf"></i> {generating ? (es ? 'Generando…' : 'Generating…') : `${state.status ? (es ? 'Volver a generar' : 'Generate again') : (es ? 'Generar' : 'Generate')} ${docType.toUpperCase()}`}
          </button>
        )}
        {!inDocuments && !state.status && !isAdmin && <span className="doc-signature-meta">{es ? 'El equipo CSR todavía no generó el PDF.' : 'The CSR team has not generated the PDF yet.'}</span>}
      </div>

      {!inDocuments && !state.status && missing.length > 0 && (
        <p className="doc-missing">{es ? 'Faltan datos (podrás completarlos al pulsar Generar): ' : 'Missing data (you can fill it in when you press Generate): '}{missing.map(m => m.label).join(', ')}</p>
      )}
      {preflight && (
        <DocumentPreflight docType={docType} claimRecord={claimRecord} estimate={preflight.estimate} language={language}
          onClose={() => setPreflight(null)} onConfirm={generate} />
      )}
      {action && (
        <div className="flow-subpanel">
        <div className="cc-grid">
          <div className="cc-input-group">
            <label className="cc-label">{action.type === 'send' ? (es ? 'Fecha de envío' : 'Sent date') : (es ? 'Fecha de firma' : 'Signed date')}</label>
            <input type="date" className="cc-input" value={action.date} onChange={e => setAction(a => ({ ...a, date: e.target.value }))} />
          </div>
          {action.type === 'send' ? (
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Enlace del documento enviado (opcional)' : 'Sent document link (optional)'}</label>
              <input type="url" className="cc-input" value={action.url} placeholder="https://" onChange={e => setAction(a => ({ ...a, url: e.target.value }))} />
            </div>
          ) : (
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'PDF firmado (máx. 15 MB)' : 'Signed PDF (max 15 MB)'}</label>
              <input type="file" accept="application/pdf" className="cc-input" onChange={e => setAction(a => ({ ...a, file: e.target.files?.[0] || null }))} />
            </div>
          )}
        </div>
          <div className="flow-actions">
            <button type="button" className="cd-btn cd-btn-check" disabled={busy || (action.type === 'sign' && !action.file)} onClick={confirm}>{es ? 'Confirmar' : 'Confirm'}</button>
            <button type="button" className="cd-btn" onClick={() => setAction(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSignature;
